/**
 * Hashtag Gallery Page - Community photos with #CatPalsPlus
 * Public page for viewing and submitting cat photos
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Heart, Camera, Upload, Star, Instagram, X, Loader2, 
  Check, Cat, User, Image as ImageIcon
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HashtagGalleryPage = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState({
    cat_name: "",
    caption: "",
    submitter_name: "",
    submitter_email: "",
    instagram_handle: "",
    adopted_from_catpals: false,
    image_base64: "",
    image_mime_type: ""
  });

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hashtag-gallery`);
      const data = await res.json();
      setPhotos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch photos:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert("Image too large. Max size is 10MB.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(",")[1];
      setFormData(prev => ({
        ...prev,
        image_base64: base64,
        image_mime_type: file.type
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image_base64) {
      alert("Please upload an image");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/hashtag-gallery`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setSubmitSuccess(true);
        setFormData({
          cat_name: "",
          caption: "",
          submitter_name: "",
          submitter_email: "",
          instagram_handle: "",
          adopted_from_catpals: false,
          image_base64: "",
          image_mime_type: ""
        });
      } else {
        const error = await res.json();
        alert(error.detail || "Failed to submit photo");
      }
    } catch (err) {
      console.error("Failed to submit:", err);
      alert("Failed to submit photo");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (photoId) => {
    try {
      await fetch(`${API_URL}/api/hashtag-gallery/${photoId}/like`, {
        method: "POST"
      });
      // Update local state
      setPhotos(prev => prev.map(p => 
        p.photo_id === photoId ? { ...p, likes: (p.likes || 0) + 1 } : p
      ));
      if (selectedPhoto?.photo_id === photoId) {
        setSelectedPhoto(prev => ({ ...prev, likes: (prev.likes || 0) + 1 }));
      }
    } catch (err) {
      console.error("Failed to like:", err);
    }
  };

  const openPhoto = (photo) => {
    setSelectedPhoto(photo);
    setShowPhotoModal(true);
  };

  const featuredPhotos = photos.filter(p => p.featured);
  const regularPhotos = photos.filter(p => !p.featured);

  return (
    <div className="min-h-screen pt-24 sm:pt-36 bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50" data-testid="hashtag-gallery-page">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 via-pink-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Camera className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
            #CatPalsPlus Gallery
          </h1>
          <p className="text-stone-600 max-w-2xl mx-auto text-lg mb-6">
            Share your adorable cat photos with our community! Whether you've adopted from CatPals+ 
            or just love cats, we'd love to see your furry friends.
          </p>
          <Button 
            onClick={() => { setSubmitSuccess(false); setShowSubmitModal(true); }}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
            size="lg"
          >
            <Upload className="w-5 h-5 mr-2" />
            Submit Your Photo
          </Button>
        </div>

        {/* Featured Section */}
        {featuredPhotos.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <Star className="w-6 h-6 text-yellow-500 fill-yellow-500" />
              <h2 className="text-2xl font-bold text-stone-900">Featured Photos</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredPhotos.map(photo => (
                <Card 
                  key={photo.photo_id}
                  className="overflow-hidden hover:shadow-xl transition-all cursor-pointer group border-2 border-yellow-200"
                  onClick={() => openPhoto(photo)}
                >
                  <div className="aspect-square relative">
                    <img
                      src={`data:${photo.image_mime_type};base64,${photo.image_base64}`}
                      alt={photo.cat_name || "Featured cat"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 right-3">
                      <Star className="w-8 h-8 text-yellow-400 fill-yellow-400 drop-shadow-lg" />
                    </div>
                    {photo.adopted_from_catpals && (
                      <div className="absolute bottom-3 left-3">
                        <Badge className="bg-purple-600 text-white shadow-lg">
                          <Cat className="w-3 h-3 mr-1" />
                          CatPals+ Cat
                        </Badge>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-lg text-stone-800">{photo.cat_name || "Adorable Cat"}</h3>
                    <p className="text-stone-500 text-sm">by {photo.submitter_name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <button 
                        className="flex items-center gap-1 text-pink-500 hover:text-pink-600 transition-colors"
                        onClick={(e) => { e.stopPropagation(); handleLike(photo.photo_id); }}
                      >
                        <Heart className="w-5 h-5" />
                        <span>{photo.likes || 0}</span>
                      </button>
                      {photo.instagram_handle && (
                        <span className="text-sm text-stone-400">{photo.instagram_handle}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Gallery */}
        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-pink-500" />
            <p className="text-stone-500 mt-4">Loading gallery...</p>
          </div>
        ) : photos.length === 0 ? (
          <Card className="max-w-md mx-auto">
            <CardContent className="p-12 text-center">
              <ImageIcon className="w-16 h-16 mx-auto text-stone-300 mb-4" />
              <h3 className="text-xl font-semibold text-stone-700 mb-2">No photos yet</h3>
              <p className="text-stone-500 mb-6">Be the first to share your cat photos!</p>
              <Button 
                onClick={() => setShowSubmitModal(true)}
                className="bg-gradient-to-r from-purple-600 to-pink-600"
              >
                <Upload className="w-4 h-4 mr-2" />
                Submit a Photo
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {regularPhotos.length > 0 && (
              <>
                <h2 className="text-2xl font-bold text-stone-900 mb-6">Community Photos</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {regularPhotos.map(photo => (
                    <Card 
                      key={photo.photo_id}
                      className="overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                      onClick={() => openPhoto(photo)}
                    >
                      <div className="aspect-square relative">
                        <img
                          src={`data:${photo.image_mime_type};base64,${photo.image_base64}`}
                          alt={photo.cat_name || "Cat photo"}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {photo.adopted_from_catpals && (
                          <div className="absolute bottom-2 left-2">
                            <Badge className="bg-purple-600/90 text-white text-xs">
                              <Cat className="w-3 h-3 mr-1" />
                              CatPals+
                            </Badge>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button 
                            className="flex items-center gap-1 text-white bg-black/30 px-2 py-1 rounded-full"
                            onClick={(e) => { e.stopPropagation(); handleLike(photo.photo_id); }}
                          >
                            <Heart className="w-4 h-4" />
                            <span className="text-sm">{photo.likes || 0}</span>
                          </button>
                        </div>
                      </div>
                      <CardContent className="p-3">
                        <p className="font-medium text-stone-800 truncate">{photo.cat_name || "Unnamed"}</p>
                        <p className="text-xs text-stone-500">by {photo.submitter_name}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-purple-100 via-pink-100 to-amber-100 rounded-2xl p-8 md:p-12">
          <h2 className="text-2xl md:text-3xl font-bold text-stone-900 mb-4">
            Adopted a CatPals+ Cat?
          </h2>
          <p className="text-stone-600 max-w-2xl mx-auto mb-6">
            We love seeing our cats in their forever homes! Share a photo and let us feature 
            your furry family member in our gallery.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => setShowSubmitModal(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Upload className="w-4 h-4 mr-2" />
              Share Your Photo
            </Button>
            <Link to="/cats">
              <Button variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50">
                <Cat className="w-4 h-4 mr-2" />
                Meet Our Cats
              </Button>
            </Link>
          </div>
        </div>

        {/* Hashtag Reminder */}
        <div className="mt-8 text-center text-stone-500 text-sm">
          <p>Tag us on social media with <span className="font-semibold text-pink-600">#CatPalsPlus</span></p>
          <p className="text-pink-500 mt-1">#NorwichCatRescue #AdoptDontShop #CatLovers</p>
        </div>
      </div>

      {/* Submit Modal */}
      <Dialog open={showSubmitModal} onOpenChange={setShowSubmitModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {submitSuccess ? "Photo Submitted!" : "Submit Your Cat Photo"}
            </DialogTitle>
          </DialogHeader>
          
          {submitSuccess ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-stone-800 mb-2">Thank you!</h3>
              <p className="text-stone-600 mb-6">
                Your photo has been submitted for review. Once approved, it will appear in our gallery!
              </p>
              <Button onClick={() => setShowSubmitModal(false)}>
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Photo *</label>
                {formData.image_base64 ? (
                  <div className="relative w-full aspect-video rounded-lg overflow-hidden border bg-stone-100">
                    <img 
                      src={`data:${formData.image_mime_type};base64,${formData.image_base64}`}
                      alt="Preview"
                      className="w-full h-full object-contain"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, image_base64: "", image_mime_type: "" }))}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full aspect-video border-2 border-dashed rounded-lg cursor-pointer hover:border-pink-500 hover:bg-pink-50/50 transition-colors">
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                    <Camera className="w-10 h-10 text-stone-400 mb-2" />
                    <span className="text-stone-500">Click to upload your photo</span>
                    <span className="text-xs text-stone-400 mt-1">Max 10MB</span>
                  </label>
                )}
              </div>

              {/* Cat Name */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Cat's Name</label>
                <Input
                  value={formData.cat_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, cat_name: e.target.value }))}
                  placeholder="What's your cat called?"
                />
              </div>

              {/* Caption */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Caption</label>
                <Textarea
                  value={formData.caption}
                  onChange={(e) => setFormData(prev => ({ ...prev, caption: e.target.value }))}
                  placeholder="Tell us about your cat..."
                  rows={2}
                />
              </div>

              {/* Your Name */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Your Name *</label>
                <Input
                  value={formData.submitter_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, submitter_name: e.target.value }))}
                  placeholder="Your name"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Email (optional)</label>
                <Input
                  type="email"
                  value={formData.submitter_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, submitter_email: e.target.value }))}
                  placeholder="your@email.com"
                />
              </div>

              {/* Instagram */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Instagram Handle (optional)</label>
                <div className="relative">
                  <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    value={formData.instagram_handle}
                    onChange={(e) => setFormData(prev => ({ ...prev, instagram_handle: e.target.value }))}
                    placeholder="@yourusername"
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Adopted from CatPals */}
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                <input
                  type="checkbox"
                  id="adopted"
                  checked={formData.adopted_from_catpals}
                  onChange={(e) => setFormData(prev => ({ ...prev, adopted_from_catpals: e.target.checked }))}
                  className="w-5 h-5 rounded text-purple-600"
                />
                <label htmlFor="adopted" className="text-stone-700 cursor-pointer">
                  <span className="font-medium">This cat was adopted from CatPals+</span>
                  <span className="block text-sm text-stone-500">Check this to get a special badge!</span>
                </label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowSubmitModal(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting || !formData.image_base64 || !formData.submitter_name}
                  className="bg-gradient-to-r from-purple-600 to-pink-600"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Submit Photo
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Detail Modal */}
      <Dialog open={showPhotoModal} onOpenChange={setShowPhotoModal}>
        <DialogContent className="max-w-2xl">
          {selectedPhoto && (
            <div className="space-y-4">
              <div className="rounded-lg overflow-hidden bg-stone-100">
                <img
                  src={`data:${selectedPhoto.image_mime_type};base64,${selectedPhoto.image_base64}`}
                  alt={selectedPhoto.cat_name || "Cat photo"}
                  className="w-full max-h-[500px] object-contain"
                />
              </div>
              
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-stone-800">
                    {selectedPhoto.cat_name || "Adorable Cat"}
                  </h3>
                  <p className="text-stone-500">
                    by {selectedPhoto.submitter_name}
                    {selectedPhoto.instagram_handle && (
                      <span className="text-pink-500 ml-2">{selectedPhoto.instagram_handle}</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {selectedPhoto.adopted_from_catpals && (
                    <Badge className="bg-purple-600 text-white">
                      <Cat className="w-3 h-3 mr-1" />
                      CatPals+ Cat
                    </Badge>
                  )}
                  {selectedPhoto.featured && (
                    <Badge className="bg-yellow-100 text-yellow-700">
                      <Star className="w-3 h-3 mr-1 fill-current" />
                      Featured
                    </Badge>
                  )}
                </div>
              </div>
              
              {selectedPhoto.caption && (
                <p className="text-stone-600">{selectedPhoto.caption}</p>
              )}
              
              <button 
                className="flex items-center gap-2 text-pink-500 hover:text-pink-600 transition-colors"
                onClick={() => handleLike(selectedPhoto.photo_id)}
              >
                <Heart className="w-6 h-6" />
                <span className="text-lg">{selectedPhoto.likes || 0} likes</span>
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HashtagGalleryPage;
